import { Category } from '@wix/answers-api';
import {
  ArticleSearchResultItem,
  ConsumerSource,
  InnerLinkComponent,
  SearchResultItemV2,
} from '@wix/cc-user-facing-common-components';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FunctionComponent } from 'react';

import { LOCALES } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { ArticleSearchResult, PageType } from '../../../types';
import { useStore } from '../../../zustand/stores';
import { InnerLink } from '../../InnerLink';
import { useSearchResultsBi } from '../../SearchResults/bi';

import css from './index.module.scss';

type SearchResultItemsProps = {
  searchResults: ArticleSearchResult;
  categoriesPaths: Record<string, Category[]>;
};

const SearchResultItems: FunctionComponent<SearchResultItemsProps> = ({
  searchResults,
  categoriesPaths,
}) => {
  const {
    query: { term },
    locale,
  } = useRouter();
  const { t } = useTranslation();
  const searchSessionId = useStore((state) => state.biSlice.searchSessionId);
  const biEvents = useSearchResultsBi(
    term as string,
    locale as string,
    searchSessionId
  );
  const onArticleTopicBadgeClick = (topic: Category, itemOrder: number) => {
    biEvents.onArticleTopicBadgeClick(topic, itemOrder);
  };

  const onArticleClick = (
    article: ArticleSearchResultItem,
    itemOrder: number
  ) => {
    biEvents.onArticleClick(
      article,
      itemOrder,
      PageType.SearchResults,
      searchResults.searchMethod
    );
  };

  return (
    <Box gap={3} direction="vertical" paddingBottom="SP3">
      <Text className={css.title}>{t('search-results-page.more-results')}</Text>
      <Box direction="vertical" className={css.searchResultItems}>
        {searchResults.items
          .slice(0, 6)
          .map((item: ArticleSearchResultItem, index: number) => {
            const rootTopic =
              item.categoryId && categoriesPaths?.[item.categoryId]?.[0];
            return (
              <Box
                key={item.id}
                className={css.searchResultItem}
                dataHook={DATA_HOOKS.SEARCH_RESULTS_V2_ITEM}
              >
                <SearchResultItemV2
                  consumerSource={ConsumerSource.HELP_CENTER}
                  searchResultItem={item}
                  innerLinkComponent={InnerLink as InnerLinkComponent}
                  locale={locale || LOCALES.EN}
                  onArticleTopicBadgeClick={(topic) =>
                    onArticleTopicBadgeClick(topic, index + 1)
                  }
                  onArticleClick={() => onArticleClick(item, index + 1)}
                  linkComponentFn={(props) => (
                    <Link
                      href={(props.href || props.to) as string}
                      onClick={props.onClick}
                      className={props.className}
                      target={props.target}
                      data-hook={props.dataHook}
                    >
                      {props.children}
                    </Link>
                  )}
                  rootTopic={rootTopic || undefined}
                />
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

export default SearchResultItems;
